import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-sm border border-[#102C1F]/25 bg-[#F4F1EB] px-4 py-2.5 text-sm text-[#102C1F] outline-none transition-colors duration-300 placeholder:text-[#102C1F]/40 focus:border-[#102C1F]";

export function useAdminAuth() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);
  return [user, setUser];
}

function formatError(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  return String(detail);
}

export function AdminLogin({ onSuccess, title = "Espace de Gestion" }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      onSuccess(data);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    }
  };

  return (
    <form
      data-testid="admin-login-form"
      onSubmit={login}
      className="w-full max-w-sm rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] p-8"
    >
      <p className="mb-2 text-xs uppercase tracking-[0.3em] text-[#102C1F]/60">
        J'aime la vie
      </p>
      <h1 className="font-display text-3xl italic tracking-tight text-[#102C1F]">
        {title}
      </h1>
      <div className="mt-6 space-y-4">
        <input
          data-testid="admin-email-input"
          type="email"
          required
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={field}
        />
        <input
          data-testid="admin-password-input"
          type="password"
          required
          placeholder="Mot de passe"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={field}
        />
        {error && (
          <p data-testid="admin-login-error" className="text-sm text-red-700">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit-btn"
          type="submit"
          className="w-full rounded-full bg-[#102C1F] py-3 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
        >
          Se connecter
        </button>
      </div>
    </form>
  );
}

export function AdminLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F4F1EB]">
      <p className="text-xs uppercase tracking-[0.3em] text-[#102C1F]/60">
        Chargement...
      </p>
    </div>
  );
}

export function playChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [523.25, 659.25, 783.99].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const t = ctx.currentTime + i * 0.12;
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.2, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.45);
    });
    setTimeout(() => ctx.close(), 1500);
  } catch {
    /* audio indisponível */
  }
}

export function useNewItemAlert(...counts) {
  const prev = useRef(null);
  useEffect(() => {
    const total = counts.reduce((a, b) => a + b, 0);
    if (prev.current !== null && total > prev.current) playChime();
    prev.current = total;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, counts);
}

export function ConfirmDelete({ item, onCancel, onConfirm }) {
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent
        data-testid="confirm-delete-popup"
        className="border-[#102C1F]/20 bg-[#F4F1EB] text-center text-[#102C1F] sm:max-w-sm"
      >
        <DialogHeader className="items-center">
          <Trash2 size={44} className="text-red-600" />
          <DialogTitle className="font-display text-3xl italic tracking-tight">
            Supprimer ?
          </DialogTitle>
          <DialogDescription className="text-sm text-[#102C1F]/65">
            {item?.name
              ? `« ${item.name} » sera supprimé définitivement.`
              : "Cette action est définitive."}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <button
            data-testid="confirm-delete-cancel-btn"
            onClick={onCancel}
            className="rounded-full border border-[#102C1F]/25 py-3 text-xs uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
          >
            Annuler
          </button>
          <button
            data-testid="confirm-delete-ok-btn"
            onClick={onConfirm}
            className="rounded-full bg-red-600 py-3 text-xs uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-red-700"
          >
            Supprimer
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
